import { Banknote, Container, Ship } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { Card } from '@/components/ui/Card'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { DashboardSummary } from '@/types/domain'
import { useTranslation } from 'react-i18next'

interface CounterProps {
  icon: LucideIcon
  label: string
  value: string
  unit?: string
  delta?: string
  /** A fall in shipments is not styled as good news. Colour is not the only
   *  carrier — the delta reads "-4" either way. */
  deltaTone?: 'positive' | 'negative'
  last?: boolean
  /** Renders the figure in muted weight, for a counter the server declared
   *  pending. The value still occupies its slot so the strip does not reflow
   *  when the figure arrives. */
  muted?: boolean
}

function Counter({
  icon: Icon,
  label,
  value,
  unit,
  delta,
  deltaTone = 'positive',
  last,
  muted,
}: CounterProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-[18px] py-3.5',
        !last && 'border-neutral-100 border-b sm:border-r sm:border-b-0',
      )}
    >
      <Icon size={16} className="text-text-muted shrink-0" aria-hidden />
      <span className="text-text-muted min-w-0 truncate text-[13px] leading-tight font-normal">
        {label}
      </span>
      <span className="flex-1" />
      <span
        className={cn(
          'asa-num text-[18px] leading-none font-semibold whitespace-nowrap',
          muted ? 'text-text-muted font-normal' : 'text-text-heading',
        )}
      >
        {value}
        {unit ? (
          <span className="text-text-muted ml-1 text-[12px] font-normal">{unit}</span>
        ) : null}
        {delta ? (
          <span
            className={cn(
              'ml-1.5 text-[12px] font-medium',
              deltaTone === 'negative' ? 'text-negative' : 'text-positive',
            )}
          >
            {delta}
          </span>
        ) : null}
      </span>
    </div>
  )
}

/** The three-counter strip above the tiles: a single card divided by hairlines,
 *  not three separate cards. */
export function CounterStrip({
  counters,
  pending,
}: {
  counters: DashboardSummary['counters']
  pending: DashboardSummary['pending']
}) {
  const { t } = useTranslation()
  /* Absent when the server declared it pending OR could not send it — see the
     comment on the counter below for why the two collapse into one state here
     and why they are resolved before either the value or the unit reads them. */
  const toPay = pending.some((p) => p.key === 'toPay') ? undefined : counters.toPay
  return (
    <Card className="grid sm:grid-cols-3">
      <Counter
        icon={Container}
        label={t('dashboard.counters.activeCases')}
        value={formatNumber(counters.activeCases)}
      />
      <Counter
        icon={Ship}
        label={t('dashboard.counters.shipmentsThisMonth')}
        value={formatNumber(counters.shipmentsThisMonth)}
        /* Signed explicitly, and tested against undefined rather than
           truthiness: a delta of 0 is a REAL figure — this month matched last
           month — and `? :` on the number itself would drop it as falsy,
           silently turning "flat" into "no baseline". */
        delta={
          counters.shipmentsDelta === undefined
            ? undefined
            : `${counters.shipmentsDelta > 0 ? '+' : ''}${formatNumber(counters.shipmentsDelta)}`
        }
        deltaTone={
          counters.shipmentsDelta !== undefined && counters.shipmentsDelta < 0
            ? 'negative'
            : 'positive'
        }
      />
      {/* "To pay" is Σ unsettled vendor-tagged actual cost lines less what has
          been paid against them (FR-30), real since 2026-08-17 and net of
          allocated payments since 2026-10-07. The em-dash branch stays rather than being deleted,
          and that is deliberate: the server can still withhold the figure when
          the read fails, and an em dash where a number goes is read as "not
          yet" where a 0 is read as "nothing to pay" — a founder who believes
          that stops chasing invoices.

          Both conditions, not just `pending`. A figure the server named in
          `pending` and a figure it simply could not send are the same thing to
          this strip, and testing only one of them would render `formatNumber(
          undefined)` for the other. Resolved ONCE into `toPay` below, so the
          value and its unit cannot disagree about whether there is a figure —
          a "USD" trailing an em dash is the shape that bug takes. */}
      <Counter
        icon={Banknote}
        label={t('dashboard.counters.toPay')}
        value={toPay ? formatNumber(toPay.amount, 2) : '—'}
        unit={toPay?.currency}
        muted={!toPay}
        last
      />
    </Card>
  )
}
